import React, { useEffect, useState } from 'react';
import { dataConstants } from '../../constants';
import { styled } from '@mui/material/styles';
import { Box, CardContent, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Typography, Link, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Slide, Button } from '@mui/material';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import getLatestPrice from '../../../lib/stock/_getLatestPrice';
import { roundNum } from '../../helpers';
import { getPercent } from '../../utils';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import WatchlistTickerSearch from './TickerSearch';
import addToWatchlist from '../../../lib/watchlist/_addToWatchlist';
import { LoadingButton } from '@mui/lab';

const convertData = (data) => {
  if (data && data?.length > 0) {
    const result = data?.reduce((acc, { ticker, r, p }) => {
      if (!acc[ticker]) {
        acc[ticker] = {
          ticker,
          price_data: [],
        };
      }

      acc[ticker]?.price_data.push({ price: p });

      return acc;
    }, {});

    return result;
  }
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  ...theme.typography.body2,
  position: 'relative',
  color: theme.palette.text.secondary,
  boxShadow: 'none',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '&:hover': {
    borderColor: theme.palette.text.disabled,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  '& .MuiGrid-root': {
    width: '100% !important',
    margin: 0,
  },
  '& .MuiGrid-root > .MuiGrid-item': {
    padding: 0,
    marginLeft: 0,
    marginRight: 0,
  },
}));

const WatchlistItem = ({
  isSystemDefault,
  accessToken,
  tickers,
  watchlistName,
  getAllWatchlists,
}) => {
  const [watchMetrics, setWatchMetrics] = useState([])
  const [loading, setLoading] = useState(false)
  const [expand, setExpand] = useState(false)
  const [_openDialog, setOpenDialog] = React.useState(false);
  const [newTickers, setNewTickers] = React.useState([]);

  const handleTickerChange = (_newTickers) => { setNewTickers(_newTickers); };

  const handleDialogClose = () => { setOpenDialog(false); };

  const handleUserWatchlist = async (tickers) => {
    setLoading(true)
    await getLatestPrice(tickers, false)
      .then(data => {
        if (data?.getLatestPrice) {
          setWatchMetrics(convertData(data?.getLatestPrice))
        }
        setLoading(false);
      })
      .catch(err => {
        console.log('caught it!', err);
        setLoading(false);
      });
  }

  const handleAddTicketsToWatchlist = async (watchlist_name, new_tickers) => {
    const _tickers = new_tickers?.map(obj => obj.t)
    const _watchlist = watchlist_name === "" ? "My Watchlist" : watchlist_name
    setLoading(true);
    await addToWatchlist(_watchlist, _tickers, null, null, null, accessToken)
      .then(data => {
        if (data?.addToWatchlist?.status === "Success") {
          getAllWatchlists()
        }
        setLoading(false);
      })
      .catch(err => {
        console.log('caught it!', err);
        setLoading(false);
      });
  }

  useEffect(() => {
    handleUserWatchlist(tickers)
  }, [tickers])

  return (
    <Item>
      <Box
        sx={{
          '& .sticky::-webkit-scrollbar': { width: 5 },
          '& .sticky': { scrollbarWidth: 'thin' },
          '& .sticky::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 },
          '& .sticky::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        }}
      >
        <CardContent sx={{ bgcolor: 'action.hover', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box component="div">
            <Typography sx={{ fontSize: 14, fontWeight: 600, mr: 1.3, mb: .3 }} color="text.color.white">
              {watchlistName}
            </Typography>
            <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
              {isSystemDefault ? 'Curated by Grufity' : tickers?.length > 0 ? `${tickers.length} stock${tickers.length !== 1 ? 's' : ''}` : ''}
            </Typography>
          </Box>
          <Link
            href={isSystemDefault ? `/watchlists/${watchlistName}` : `/watchlists/u/${watchlistName}`}
            underline="none"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              fontSize: '0.8rem', fontWeight: 500, color: 'primary.main',
              px: 1.5, py: 0.5, borderRadius: 5,
              border: '1px solid', borderColor: 'divider',
              transition: 'all 0.15s',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            View all <EastRoundedIcon sx={{ fontSize: '0.85rem' }} />
          </Link>
        </CardContent>
        <TableContainer className="sticky" sx={{ maxHeight: expand ? '100%' : 440, overflow: 'hidden' }}>
          <Table stickyHeader aria-label={`${watchlistName.replace(' ', '_')}`} size="small">
            <TableHead sx={{ '& th': { bgcolor: 'background.paper', color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.03em' } }}>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Change</TableCell>
                <TableCell align="right">Chg %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ '& .ticker-name': { whiteSpace: 'nowrap', position: 'absolute', } }} >
              {tickers && tickers?.length > 0 ?
                tickers?.length == 1 && tickers?.includes('') ?
                  <StyledTableRow sx={{ '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
                    <TableCell colSpan={4} align="center">
                      <LoadingButton
                        size="small"
                        loading={loading}
                        loadingPosition="start"
                        sx={{ textTransform: 'none' }}
                        startIcon={<AddRoundedIcon />}
                        onClick={() => setOpenDialog(true)}
                      >
                        <span>Add Tickers</span>
                      </LoadingButton>
                    </TableCell>
                  </StyledTableRow> :
                  tickers?.map((d) => {
                    const _ticker = watchMetrics ? watchMetrics[d] : null;
                    return d !== "" ? (
                      <StyledTableRow hover key={d} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell align="left" sx={{ '& a': { color: 'text.link' } }}>
                          <Link underline="hover" sx={{ fontWeight: 600 }} href={`${dataConstants.NAVS.STOCK}/${d}`}>
                            {d}
                          </Link>
                        </TableCell>
                        <TableCell align="right">{_ticker?.price_data?.length > 0 ? `$${roundNum(_ticker?.price_data[0]?.price)}` : '-'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: _ticker?.price_data[0]?.price >= _ticker?.price_data[1]?.price ? 'success.main' : 'error.main' }}>
                          {_ticker?.price_data[0]?.price - _ticker?.price_data[1]?.price >= 0
                            ? `+${roundNum(_ticker?.price_data[0]?.price - _ticker?.price_data[1]?.price)}`
                            : _ticker?.price_data[0]?.price && _ticker?.price_data[1]?.price ? roundNum(_ticker?.price_data[0]?.price - _ticker?.price_data[1]?.price) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: _ticker?.price_data[0]?.price >= _ticker?.price_data[1]?.price ? 'success.main' : 'error.main' }}>
                          {_ticker?.price_data?.length > 0 && _ticker?.price_data[0]?.price && _ticker?.price_data[1]?.price ? `${getPercent(_ticker?.price_data[0]?.price, _ticker?.price_data[1]?.price)}%` : '-'}
                        </TableCell>
                      </StyledTableRow>
                    ) : null;
                  }) :
                <StyledTableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell colSpan={4} align="center">
                    <LoadingButton
                      size="small"
                      loading={loading}
                      loadingPosition="start"
                      sx={{ textTransform: 'none' }}
                      startIcon={<AddRoundedIcon />}
                      onClick={() => setOpenDialog(true)}
                    >
                      <span>Add Tickers</span>
                    </LoadingButton>
                  </TableCell>
                </StyledTableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {tickers && tickers?.length > 13 && !expand &&
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, width: '100%', height: '6rem',
            borderRadius: 1, zIndex: 10,
            background: 'linear-gradient(transparent, rgba(255,255,255,0.95) 70%)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}>
            <Chip
              label="View More"
              size="small"
              onClick={() => setExpand(true)}
              onDelete={() => setExpand(true)}
              deleteIcon={<KeyboardArrowDownRoundedIcon color="inherit" />}
              sx={{
                my: 2, bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                color: '#fff',
                '& .MuiChip-deleteIcon, .MuiChip-deleteIcon:hover': { color: '#fff' },
              }}
            />
          </Box>}
      </Box>
      <Dialog
        open={_openDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleDialogClose}
        aria-describedby="alert-dialog-slide-description"
        onSubmit={(e) => { e.preventDefault(); handleAddTicketsToWatchlist(watchlistName, newTickers) }}
      >
        <DialogTitle>{`Add tickers to ${watchlistName}`}</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            sx={{
              '& .MuiTextField-root': { width: '100%' },
            }}
            noValidate
            autoComplete="off"
            onSubmit={(e) => { e.preventDefault(); handleAddTicketsToWatchlist(watchlistName, newTickers); setOpenDialog(false); }}
          >
            <WatchlistTickerSearch
              selectedWatchlist={watchlistName}
              // setShowSearch={setShowSearch}
              removebackdrop={false}
              handleTickerChange={handleTickerChange}
              tickers={newTickers}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ mb: 1, pt: 0 }}>
          <Button sx={{ textTransform: 'none', mr: 2 }} onClick={handleDialogClose}>Cancel</Button>
          <Button variant="contained" sx={{ textTransform: 'none', mr: 2 }} onClick={() => { handleAddTicketsToWatchlist(watchlistName, newTickers); setOpenDialog(false); }} disabled={newTickers && newTickers.length === 0}>Add Tickers</Button>
        </DialogActions>
      </Dialog>
    </Item>)
}

export default WatchlistItem;
